import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { API_URL } from "../../config.js";
import Notification from "../../components/Notification.js";
import ConfirmDialog from "../../components/ConfirmDialog";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCalendarAlt, faUsers, faUser, faUserTie, faEnvelope, faPhone, faHashtag, faCalendarDay, faClipboard, faUserGroup
} from '@fortawesome/free-solid-svg-icons';

function BookingDetails() {
    const [booking, setBooking] = useState({
        id: "",
        customer_name: "",
        booking_date: "",
        status: "",
        total_amount: 0,
        details: [],
    });
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState("");
    const [confirmDialogTitle, setConfirmDialogTitle] = useState("");
    const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();

    const fetchBooking = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/bookings/id/${id}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            });
            if (response.ok) {
                const data = await response.json();
                setBooking(data);
            } else if (response.status === 404) {
                setNotification({ type: "danger", message: "Booking not found." });
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else {
                throw new Error("Failed to fetch booking.");
            }
        } catch (error) {
            setNotification({ type: "danger", message: "An error occurred while fetching the booking." });
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        fetchBooking();
    }, [fetchBooking]);

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const handleAction = (actionType) => {
        setConfirmAction(actionType);

        let title = "Confirm Action";
        let message = `Are you sure you want to ${actionType} this booking?`;

        if (actionType === "cancel") {
            title = "Cancel Booking";
            message = "Are you sure you want to cancel this booking?";
        }

        setConfirmDialogTitle(title);
        setConfirmDialogMessage(message);
        setConfirmDialogOpen(true);
    };

    const handleConfirmation = async () => {
    }
    const confirmActionHandler = async () => {
        let endpoint = `${API_URL}/bookings/${id}`;

        if (confirmAction === "cancel") {
            endpoint += "/cancel";
        }

        try {
            const response = await fetch(endpoint, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            });

            if (response.ok) {
                setNotification({ type: "success", message: `Booking ${confirmAction}ed successfully.` });
                await fetchBooking();
            } else {
                setNotification({ type: "danger", message: `Failed to ${confirmAction} the booking.` });
            }
        } catch (error) {
            setNotification({ type: "danger", message: "An error occurred while processing the request." });
        } finally {
            setConfirmDialogOpen(false);
        }
    };

    if (loading) return <p>Loading...</p>;

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">Booking Details</p>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/bookings" className="button primary-bg">
                            <i className="fa fa-arrow-left" aria-hidden="true"></i> &nbsp; Back
                        </Link>
                        &nbsp; &nbsp;
                        <Link to={`/dashboard/bookings/${booking.id}/update`} className="button primary-bg">
                            <i className="fa fa-pencil" aria-hidden="true"></i> &nbsp; Edit
                        </Link>
                    </p>
                </div>
            </nav>

            {notification.message && (
                <Notification
                    type={notification.type}
                    message={notification.message}
                    onClose={closeNotification}
                />
            )}

            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                theme="is-primary"
                title={confirmDialogTitle}
                message={confirmDialogMessage}
                onConfirm={handleConfirmation}
                onCancel={() => setConfirmDialogOpen(false)}
                isLoading={false}
            />

            <div className="columns is-multiline mb-6">
                {/* Booking Header */}
                <div className="column is-12">
                    <div className="box has-text-centered">
                        <p className="title is-3">{booking.group_name || booking.individual_name}</p>
                        {booking.status && (
                            <p className={`tag has-text-weight-bold is-${booking.status === "confirmed" || booking.status === "PendingApproval" ? "success" : "danger"}`}>
                                {booking.status.toUpperCase()}
                            </p>
                        )}
                        <p>
                            Booked by: {booking.individual_name} - {booking.email} ({booking.phone_number})
                        </p>
                    </div>
                </div>


                <div className="column is-6">
                    <div className="box">
                        <p className="title is-5">
                            <FontAwesomeIcon icon={faUser} /> &nbsp; Booked By
                        </p>
                        <div className="content">
                            <div className="field mb-4">
                                <p className="control">
                                    <span className="icon-text">
                                        <span className="icon">
                                            <FontAwesomeIcon icon={booking.booking_type === "Individual" ? faUser : faUsers} />
                                        </span>
                                        <span>
                                            <strong>Type:</strong> {booking.booking_type}
                                        </span>
                                    </span>
                                </p>
                            </div>

                            {booking.booking_type === "Individual" ? (
                                <div className="field mb-4">
                                    <p className="control">
                                        <span className="icon-text">
                                            <span className="icon">
                                                <FontAwesomeIcon icon={faUser} />
                                            </span>
                                            <span>
                                                <strong>Name:</strong> {booking.individual_name}
                                            </span>
                                        </span>
                                    </p>
                                </div>
                            ) : (
                                <div className="field mb-4">
                                    <p className="control">
                                        <span className="icon-text">
                                            <span className="icon">
                                                <FontAwesomeIcon icon={faUsers} />
                                            </span>
                                            <span>
                                                <strong>Group Name:</strong> {booking.group_name}
                                            </span>
                                        </span>
                                    </p>
                                </div>
                            )}

                            {/* Contact person for groups */}
                            {booking.booking_type === "Group" && booking.individual_name && (
                                <div className="field mb-4">
                                    <p className="control">
                                        <span className="icon-text">
                                            <span className="icon">
                                                <FontAwesomeIcon icon={faUserTie} />
                                            </span>
                                            <span>
                                                <strong>Contact Person:</strong> {booking.individual_name}
                                            </span>
                                        </span>
                                    </p>
                                </div>
                            )}


                            <div className="field mb-4">
                                <p className="control">
                                    <span className="icon-text">
                                        <span className="icon">
                                            <FontAwesomeIcon icon={faUserGroup} />
                                        </span>
                                        <span>
                                            <strong>Number of People:</strong> {booking.number_of_people}
                                        </span>
                                    </span>

                                </p>
                            </div>

                            {/* Contact info */}
                            <div className="field mb-4">
                                <p className="control">
                                    <span className="icon-text">
                                        <span className="icon">
                                            <FontAwesomeIcon icon={faEnvelope} />
                                        </span>
                                        <span>
                                            <strong>Email:</strong> {booking.email}
                                        </span>
                                    </span>
                                </p>
                            </div>

                            <div className="field">
                                <p className="control">
                                    <span className="icon-text">
                                        <span className="icon">
                                            <FontAwesomeIcon icon={faPhone} />
                                        </span>
                                        <span>
                                            <strong>Phone:</strong> {booking.phone_number}
                                        </span>
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="column is-6">
                    {/* Dates */}
                    <div className="box mb-4">
                        <p className="title is-5">
                            <FontAwesomeIcon icon={faCalendarAlt} /> &nbsp; Dates
                        </p>
                        <div className="content">
                            <div className="field mb-3">
                                <p className="control">
                                    <span className="icon-text">
                                        <span className="icon">
                                            <FontAwesomeIcon icon={faCalendarDay} />
                                        </span>
                                        <span>
                                            <strong>From:</strong> {booking.start_date}
                                        </span>
                                    </span>
                                </p>
                            </div>
                            <div className="field">
                                <p className="control">
                                    <span className="icon-text">
                                        <span className="icon">
                                            <FontAwesomeIcon icon={faCalendarDay} />
                                        </span>
                                        <span>
                                            <strong>To:</strong> {booking.end_date}
                                        </span>
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>


                    {/* Numbers */}
                    <div className="box mb-4">
                        <p className="title is-5">
                            <FontAwesomeIcon icon={faUsers} /> &nbsp; Details
                        </p>
                        <div className="content">
                            <div className="field">
                                <p className="control">
                                    <span className="icon-text">
                                        <span className="icon">
                                            <FontAwesomeIcon icon={faHashtag} />
                                        </span>
                                        <span>
                                            <strong>Reference No:</strong> {booking.reference_no}
                                        </span>
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
                <div className="column is-12">
                    {/* Special Requests */}
                    <div className="box">
                        <p className="title is-5">
                            <FontAwesomeIcon icon={faClipboard} /> &nbsp; Special Requests
                        </p>
                        <p className="content">
                            {booking.special_requests || "No special requests"}
                        </p>
                    </div>

                    {/* Comment */}
                    <div className="box">
                        <p className="title is-5">
                            <FontAwesomeIcon icon={faClipboard} /> &nbsp; Comment
                        </p>
                        <p className="content">
                            {booking.comment || "No Comment"}
                        </p>
                    </div>
                </div>
            </div>
        </main>
    );
}

export default BookingDetails;
